import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  reload,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import Logo from '../components/Logo';
import AuthArtPanel from '../components/AuthArtPanel';

const MIN_PASSWORD = 8; // Match Login.js — length over forced complexity.

// Custom email action handler. Firebase (Authentication > Templates >
// customize action URL) is configured to point every account email link
// at this page: /auth/action?mode=<action>&oobCode=<code>. It replaces
// Google's unstyled firebaseapp.com handler so the reset flow stays on
// our domain and on-brand.
//
// We fully implement `resetPassword`, `verifyEmail` and
// `verifyAndChangeEmail`. Anything else points the user back to sign in.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');

  // phase: 'verifying' | 'form' | 'saving' | 'done' | 'invalid'
  const [phase, setPhase] = useState('verifying');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [headline, setHeadline] = useState('');
  const [detail, setDetail] = useState('');
  // True when the link was opened in a browser that is still signed in, so
  // the success screen can offer the app instead of a sign-in page.
  const [stillSignedIn, setStillSignedIn] = useState(false);

  // Step 1: validate the action code on mount.
  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!oobCode) {
        if (!cancelled) invalidLink();
        return;
      }

      if (mode === 'resetPassword') {
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (cancelled) return;
          setAccountEmail(email);
          setPhase('form');
        } catch {
          if (!cancelled) invalidLink();
        }
        return;
      }

      if (mode === 'verifyEmail') {
        try {
          await applyActionCode(auth, oobCode);
          if (cancelled) return;
          // A signed-in user's cached token still says unverified; refresh
          // the record so the app stops prompting the moment they go back.
          if (auth.currentUser) {
            try { await reload(auth.currentUser); } catch { /* non-fatal */ }
          }
          setStillSignedIn(!!auth.currentUser);
          setHeadline('Email confirmed');
          setDetail('Thanks — your email address is confirmed. Alerts about your tank will reach this address.');
          setPhase('done');
        } catch {
          if (!cancelled) invalidLink();
        }
        return;
      }

      // Email change. The address only moves at this point — up to now the
      // account still signed in with the old one, so an abandoned or
      // mistyped change can't lock anybody out.
      //
      // Whoever clicks this is often not in the browser that asked for the
      // change (it's whatever opened the mail), and may have no session at
      // all. So we read the new address off the code BEFORE consuming it,
      // then reconcile Firestore's copies of it one of two ways: as the
      // signed-in user if we have one, otherwise through the public
      // finishEmailChange, which can only mirror a change Auth already
      // accepted. See functions/emailSync.js.
      if (mode === 'verifyAndChangeEmail') {
        let newEmail = '';
        try {
          const info = await checkActionCode(auth, oobCode);
          newEmail = info?.data?.email || '';
        } catch {
          if (!cancelled) invalidLink();
          return;
        }
        try {
          await applyActionCode(auth, oobCode);
        } catch {
          if (!cancelled) invalidLink();
          return;
        }
        if (cancelled) return;

        // Fan the new address out to the alert/recap recipient copies.
        // Best-effort in both branches: an hourly reconciler on the server
        // picks up anything that fails here, so a hiccup delays alerts to
        // the new address rather than losing them.
        let synced = false;
        if (auth.currentUser) {
          try {
            await reload(auth.currentUser);
            await auth.currentUser.getIdToken(true);
            await httpsCallable(functions, 'syncMyEmail')();
            synced = true;
          } catch (err) {
            console.error('syncMyEmail after email change failed:', err);
          }
        }
        if (!synced && newEmail) {
          try {
            await httpsCallable(functions, 'finishEmailChange')({ email: newEmail });
          } catch (err) {
            console.error('finishEmailChange failed:', err);
          }
        }

        if (cancelled) return;
        setStillSignedIn(synced);
        setAccountEmail(newEmail);
        setHeadline('Email updated');
        setDetail(
          newEmail
            ? `Your account now uses ${newEmail}. That's the address you sign in with from now on — your password hasn't changed.`
            : "Your account email has been updated. That's the address you sign in with from now on — your password hasn't changed."
        );
        setPhase('done');
        return;
      }

      // Unknown / unsupported mode — send them to sign in.
      if (!cancelled) {
        setHeadline('Link not recognized');
        setDetail('This link isn’t something we can handle here. Head back to sign in and try again.');
        setPhase('invalid');
      }
    }

    // Expired/used/garbled code. The remedy differs by what the link was
    // for, and "request a new one from the sign-in page" is wrong advice for
    // anything but a password reset — these links are one-shot and short-lived,
    // so people do land here.
    function invalidLink() {
      setHeadline('This link has expired');
      if (mode === 'verifyAndChangeEmail') {
        setDetail('Email change links can only be used once, and expire after an hour. Your account email has not changed. Sign in and request the change again from Settings → Account.');
      } else if (mode === 'verifyEmail') {
        setDetail('Confirmation links can only be used once, and expire after an hour. Sign in and we’ll send you a fresh one.');
      } else {
        setDetail('Password reset links are valid for one hour and can only be used once. Request a new one from the sign-in page.');
      }
      setPhase('invalid');
    }

    verify();
    return () => { cancelled = true; };
  }, [mode, oobCode]);

  // Step 2: set the new password.
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Those passwords don’t match. Please re-enter them.');
      return;
    }

    setPhase('saving');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setHeadline('Password updated');
      setDetail('Your password has been changed. You can now sign in with your new password.');
      setPhase('done');
    } catch (err) {
      const code = err?.code;
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        setHeadline('This link has expired');
        setDetail('Password reset links are valid for one hour and can only be used once. Request a new one from the sign-in page.');
        setPhase('invalid');
      } else if (code === 'auth/weak-password') {
        setError('That password is too weak. Try a longer one.');
        setPhase('form');
      } else {
        setError('Something went wrong updating your password. Please try again.');
        setPhase('form');
      }
    }
  }

  return (
    <div className="login-shell">

      {/* Left — brand / value panel (desktop only), mirrors the login page */}
      <AuthArtPanel />

      {/* Right — reset form */}
      <div className="login-form-wrap">
        <div className="login-form-inner">
          <div className="login-form-brand">
            <Logo variant="primary" width={170} />
          </div>

          {phase === 'verifying' && (
            <>
              <h1>Checking your link…</h1>
              <p className="login-lead">One moment.</p>
            </>
          )}

          {phase === 'form' && (
            <>
              <h1>Choose a new password</h1>
              <p className="login-lead">
                {accountEmail ? `for ${accountEmail}` : 'Set a new password for your account.'}
              </p>

              {error && (
                <div className="alert-banner fault" style={{ marginBottom: 14 }}>
                  <div className="alert-banner-desc">{error}</div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label>New password</label>
                  <input
                    type="password"
                    placeholder={`At least ${MIN_PASSWORD} characters`}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label>Confirm new password</label>
                  <input
                    type="password"
                    placeholder="Re-enter your new password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Update password
                </button>
              </form>
            </>
          )}

          {phase === 'saving' && (
            <>
              <h1>Updating your password…</h1>
              <p className="login-lead">One moment.</p>
            </>
          )}

          {phase === 'done' && (
            <>
              <h1>{headline}</h1>
              <div className="toast" style={{ margin: '14px 0' }}>
                <span>✓</span> {detail}
              </div>
              <div className="login-switch">
                {stillSignedIn ? (
                  <Link className="login-switch__primary" to="/dashboard">Back to your tanks</Link>
                ) : (
                  <Link className="login-switch__primary" to="/login">Go to sign in</Link>
                )}
              </div>
            </>
          )}

          {phase === 'invalid' && (
            <>
              <h1>{headline}</h1>
              <p className="login-lead">{detail}</p>
              <div className="login-switch">
                <Link className="login-switch__primary" to="/login">Back to sign in</Link>
              </div>
            </>
          )}

          <p className="login-foot-line">SoKo Water Systems · Know your water.</p>
        </div>
      </div>
    </div>
  );
}
